import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PARTNER_STATUS_OPTIONS } from '../../../config/enum-options.js';
import EntityLink from '../../../shared/ui/entity-link/entity-link.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';
import { getPartnerStatusBadgeClass } from './partner-status-badge.js';

function PartnersPageView({
  rows, isLoading, error, statusFilter, isFilterOpen,
  onStatusFilterChange, toggleFilter, resetFilters, reload,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div>
      <FloatingAlerts error={error} />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('partners.title')}</h2>
          <p>{t('partners.subtitle')}</p>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={toggleFilter}>
            <i className="ti ti-filter me-1"></i>{t('profiles.filters')}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={reload} disabled={isLoading}>
            {t('partners.refresh')}
          </button>
        </div>
      </div>

      {isFilterOpen ? (
        <div className="card card-body mb-3">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">{t('common.status')}</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
              >
                {PARTNER_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.label)}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <button type="button" className="btn btn-outline-secondary" onClick={resetFilters}>
                {t('common.reset')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="widget-content searchable-container list">
        <div className="card card-body">
          <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
            <table className="table search-table align-middle text-nowrap mb-0">
              <thead className="header-item">
                <tr>
                  <th>{t('common.name')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('partners.referralCode')}</th>
                  <th>{t('partners.commissionPlan')}</th>
                  <th>{t('partners.walletBalance')}</th>
                  <th>{t('common.date')}</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">{t('partners.noPartnersFound')}</td>
                  </tr>
                ) : null}
                {rows.map((row) => (
                  <tr
                    key={row.key}
                    className="app-clickable-row"
                    onClick={() => navigate(`/partners/${encodeURIComponent(row.uid)}`)}
                  >
                    <td>
                      <EntityLink
                        to={`/profiles/${encodeURIComponent(row.profileUid)}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.profileName}
                      </EntityLink>
                    </td>
                    <td>
                      <span className={`badge rounded-pill fw-semibold text-uppercase ${getPartnerStatusBadgeClass(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>{row.referralCode}</td>
                    <td>
                      {row.commissionPlanUid ? (
                        <EntityLink
                          to={`/commission-plans/${encodeURIComponent(row.commissionPlanUid)}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.commissionPlanName}
                        </EntityLink>
                      ) : (
                        row.commissionPlanName
                      )}
                    </td>
                    <td>{row.walletBalanceFormatted}</td>
                    <td>{row.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </LoadingContainer>
        </div>
      </div>
    </div>
  );
}

export default PartnersPageView;
